import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
const HISTORY_INDEX = '__pplxHistoryIndex';
const HISTORY_EPOCH = '__pplxHistoryEpoch';
function historyMetadata(state) {
    if (typeof state !== 'object' ||
        state === null ||
        !(HISTORY_INDEX in state) ||
        !(HISTORY_EPOCH in state))
        return;
    const index = state[HISTORY_INDEX];
    const epoch = state[HISTORY_EPOCH];
    if (typeof index !== 'number' || !Number.isInteger(index) || typeof epoch !== 'string')
        return;
    return { index, epoch };
}
function trackHistory() {
    const { history } = window;
    const pushState = history.pushState.bind(history);
    const replaceState = history.replaceState.bind(history);
    // metadata writes must not notify wouter before the dirty-edit guard handles popstate.
    const replaceMetadata = History.prototype.replaceState.bind(history);
    const go = history.go.bind(history);
    const initial = historyMetadata(history.state);
    let epoch = initial?.epoch ?? safeRandomUUID();
    const stamp = (index) => replaceMetadata({ ...history.state, [HISTORY_INDEX]: index, [HISTORY_EPOCH]: epoch }, '');
    const position = (index, key = Symbol()) => ({
        index,
        epoch,
        pathname: window.location.pathname,
        url: window.location.href,
        state: history.state,
        key,
    });
    // index entries before a feature becomes dirty, including the initial document entry.
    stamp(initial?.index ?? 0);
    let current = position(initial?.index ?? 0);
    const entryKeys = new Map([[current.index, current.key]]);
    let lastIndex = current.index;
    let programmaticIndex;
    let traversal;
    history.pushState = (data, unused, url) => {
        const index = current.index + 1;
        pushState({ ...data, [HISTORY_INDEX]: index, [HISTORY_EPOCH]: epoch }, unused, url);
        current = position(index);
        entryKeys.set(index, current.key);
        lastIndex = index;
    };
    history.replaceState = (data, unused, url) => {
        replaceState({ ...data, [HISTORY_INDEX]: current.index, [HISTORY_EPOCH]: epoch }, unused, url);
        current = position(current.index, current.key);
    };
    const programmaticGo = (delta = 0) => {
        const index = current.index + delta;
        // out-of-range calls do not emit popstate; do not exempt a later browser action.
        programmaticIndex = delta !== 0 && index >= 0 && index <= lastIndex ? index : undefined;
        go(delta);
    };
    history.go = programmaticGo;
    history.back = () => programmaticGo(-1);
    history.forward = () => programmaticGo(1);
    window.addEventListener('popstate', (event) => {
        traversal = undefined;
        const expectedIndex = programmaticIndex;
        programmaticIndex = undefined;
        const from = current;
        const metadata = historyMetadata(event.state);
        let index = metadata?.epoch === epoch ? metadata.index : undefined;
        const unindexed = index === undefined;
        if (index === undefined) {
            // reload can retain entries created before tracking existed. their distance and
            // direction are unknown, so start a fresh coordinate space rather than guess.
            epoch = safeRandomUUID();
            entryKeys.clear();
            index = 0;
            lastIndex = 0;
            stamp(index);
        }
        current = position(index, entryKeys.get(index));
        entryKeys.set(index, current.key);
        lastIndex = Math.max(lastIndex, index);
        traversal = { from, to: current, programmatic: !unindexed && expectedIndex === index };
    }, true);
    const goTo = (destination) => {
        // a push while confirmation is open can replace the forward entry at this index.
        if (destination.epoch !== epoch ||
            destination.index === current.index ||
            destination.index > lastIndex ||
            entryKeys.get(destination.index) !== destination.key)
            return;
        go(destination.index - current.index);
    };
    return {
        getEntryKey: () => current.key,
        registerGuard(shouldBlock, requestNavigation) {
            let restoring;
            const onPopState = (event) => {
                if (!traversal)
                    return;
                const origin = restoring?.origin ?? traversal.from;
                if (!restoring &&
                    (!shouldBlock() || traversal.programmatic || origin.pathname === traversal.to.pathname))
                    return;
                event.stopImmediatePropagation();
                if (origin.epoch !== traversal.to.epoch) {
                    // unknown positions cannot be undone with go(delta). recreating the origin
                    // keeps its draft mounted, at the cost of resetting the forward branch.
                    const destination = traversal.to;
                    restoring = undefined;
                    history.pushState(origin.state, '', origin.url);
                    requestNavigation(() => goTo(destination));
                    return;
                }
                if (restoring) {
                    if (traversal.to.index !== restoring.origin.index) {
                        goTo(restoring.origin);
                        return;
                    }
                    const { destination } = restoring;
                    restoring = undefined;
                    requestNavigation(() => goTo(destination));
                    return;
                }
                // popstate cannot be canceled. restore the entry before the router observes it,
                // then replay the same traversal only after the user accepts the discard dialog.
                restoring = { origin: traversal.from, destination: traversal.to };
                goTo(restoring.origin);
            };
            window.addEventListener('popstate', onPopState, true);
            return () => window.removeEventListener('popstate', onPopState, true);
        },
    };
}
let legacyHistory;
export function getLegacyHistory() {
    if (typeof window === 'undefined' || window.navigation)
        return;
    // history belongs to the document; guard registrations still belong to each router.
    return (legacyHistory ??= trackHistory());
}
export function getHistoryEntryKey() {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return window.navigation?.currentEntry?.key ?? legacyHistory?.getEntryKey();
}
//# sourceMappingURL=legacyHistory.js.map